import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Stack, Textarea, Group, Button, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

interface AddNoteModalProps {
  opened: boolean;
  onClose: () => void;
  addNote: (text: string) => Promise<void>;
}

export function AddNoteModal({ opened, onClose, addNote }: AddNoteModalProps) {
  const { t } = useTranslation('lims');

  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleAddNote = async () => {
    if (!noteText.trim()) {
      return;
    }
    try {
      setNoteSaving(true);
      setLocalError(null);
      await addNote(noteText.trim());
      handleClose();
    } catch (e: any) {
      setLocalError(e.message || 'Failed to add note');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleClose = () => {
    setNoteText('');
    setLocalError(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('payment.addNote')}
      centered
      size="sm"
    >
      <Stack gap="md">
        {localError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {localError}
          </Alert>
        )}

        <Textarea
          label={t('payment.labels.noteText')}
          placeholder={t('payment.placeholders.enterNote')}
          value={noteText}
          onChange={(e) => setNoteText(e.currentTarget.value)}
          minRows={3}
        />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleClose}>
            {t('payment.cancel')}
          </Button>
          <Button
            onClick={handleAddNote}
            loading={noteSaving}
            disabled={!noteText.trim()}
          >
            {t('payment.confirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
